import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRotateRight,
  faCheck,
  faGripVertical,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from '@/lib/swal';
import {
  inspectionReportService,
  type AvailableReportFinding,
} from '@/services/inspection-report.service';

interface Props {
  inspectionId: string;
  canEdit: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function isForbidden(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

/** Severity is reference data from the finding; shown as a neutral chip so
 *  selection is never communicated by colour alone. */
function SeverityChip({ severity }: { severity: string }) {
  return (
    <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {severity}
    </span>
  );
}

function SortableSelectedFinding({
  finding,
  index,
  canEdit,
  onRemove,
}: {
  finding: AvailableReportFinding;
  index: number;
  canEdit: boolean;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: finding.id,
    disabled: !canEdit,
  });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2"
      data-testid={`selected-finding-${finding.id}`}
    >
      <span className="w-5 shrink-0 text-xs font-semibold text-muted-foreground">{index + 1}.</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-foreground">{finding.title}</p>
        {finding.componentArea ? (
          <p className="truncate text-xs text-muted-foreground">{finding.componentArea}</p>
        ) : null}
      </div>
      <SeverityChip severity={finding.severity} />
      {canEdit && (
        <>
          <button
            type="button"
            onClick={onRemove}
            className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid={`deselect-finding-${finding.id}`}
          >
            Remove
          </button>
          <button
            type="button"
            className="cursor-grab rounded px-1 py-1 text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={`Reorder ${finding.title}`}
            data-testid={`drag-finding-${finding.id}`}
            {...attributes}
            {...listeners}
          >
            <FontAwesomeIcon icon={faGripVertical} className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </li>
  );
}

/**
 * Finding Composer — decides WHICH findings appear in the report and in WHAT
 * order. Follows ReportPhotoSelector's visual language and @dnd-kit ordering.
 *
 * One deliberate difference from the photo selector: this composer holds a local
 * draft and saves only when the engineer presses Save. Photos save on every
 * click, which is safe because photo selection starts empty. Findings do not —
 * a report that has never been composed renders EVERY finding, and auto-saving
 * on open would silently convert that legacy report into an explicit selection
 * frozen at that moment. `findingsComposed` therefore flips only on Save.
 */
export function ReportFindingSelector({ inspectionId, canEdit }: Props) {
  const qc = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Keyboard reordering: Space to lift, arrows to move, Space to drop.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const compositionQuery = useQuery({
    queryKey: ['report-findings', inspectionId],
    queryFn: () => inspectionReportService.getFindingComposition(inspectionId),
    retry: false,
    staleTime: 10_000,
  });
  const availableQuery = useQuery({
    queryKey: ['report-findings-available', inspectionId],
    queryFn: () => inspectionReportService.getAvailableFindings(inspectionId),
    retry: false,
    staleTime: 10_000,
  });

  const composition = compositionQuery.data;
  const available = useMemo(() => availableQuery.data ?? [], [availableQuery.data]);

  /** Working draft: ordered finding ids. Not persisted until Save. */
  const [draft, setDraft] = useState<string[] | null>(null);

  useEffect(() => {
    if (!composition || availableQuery.isLoading) return;
    setDraft((current) => {
      if (current !== null) return current; // never clobber in-progress edits
      if (composition.findingsComposed) {
        return composition.selected.map((s) => s.inspectionFindingId);
      }
      // Legacy report: seed the draft with everything, matching what the report
      // renders today. Nothing is written until Save.
      return available.map((f) => f.id);
    });
  }, [composition, available, availableQuery.isLoading]);

  const byId = useMemo(() => new Map(available.map((f) => [f.id, f])), [available]);
  const selectedIds = draft ?? [];
  const selectedFindings = selectedIds
    .map((id) => byId.get(id))
    .filter((f): f is AvailableReportFinding => !!f);

  const saveMutation = useMutation({
    mutationFn: (ids: string[]) => inspectionReportService.setFindings(inspectionId, ids),
    onSuccess: (result) => {
      // Re-seed the draft from the server so ordering and membership match it.
      setDraft(result.selected.map((s) => s.inspectionFindingId));
      qc.setQueryData(['report-findings', inspectionId], result);
      void qc.invalidateQueries({ queryKey: ['report-findings-available', inspectionId] });
      toast.success('Report findings updated');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to update report findings')),
  });

  const busy = saveMutation.isPending;

  const toggle = (id: string) =>
    setDraft((cur) => {
      const list = cur ?? [];
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedIds.indexOf(String(active.id));
    const newIndex = selectedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    setDraft(arrayMove(selectedIds, oldIndex, newIndex));
  };

  // ── Loading / error ────────────────────────────────────────────────────────
  // A failure must never look like "no findings".
  const error = compositionQuery.error ?? availableQuery.error;
  if (error) {
    const forbidden = isForbidden(compositionQuery.error) || isForbidden(availableQuery.error);
    return (
      <div className="space-y-2" data-testid="report-finding-selector-error">
        <h3 className="text-sm font-semibold text-foreground">Report Findings</h3>
        <p className="text-sm text-destructive">
          {forbidden
            ? 'You do not have permission to view the report findings.'
            : extractMsg(error as Error, 'Could not load the report findings.')}
        </p>
        {!forbidden && (
          <button
            type="button"
            onClick={() => {
              void compositionQuery.refetch();
              void availableQuery.refetch();
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="retry-report-findings"
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="h-3 w-3" />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (compositionQuery.isLoading || availableQuery.isLoading || draft === null) {
    return (
      <div className="space-y-2" data-testid="report-finding-selector-loading">
        <h3 className="text-sm font-semibold text-foreground">Report Findings</h3>
        <p className="text-sm text-muted-foreground">Loading findings…</p>
      </div>
    );
  }

  const savedIds = composition?.findingsComposed
    ? composition.selected.map((s) => s.inspectionFindingId)
    : null;
  const dirty =
    savedIds === null || savedIds.length !== selectedIds.length
      ? true
      : savedIds.some((id, i) => id !== selectedIds[i]);

  return (
    <div className="space-y-4" data-testid="report-finding-selector">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">Report Findings</h3>
        <span className="text-xs text-muted-foreground" data-testid="finding-selection-count">
          Selected: {selectedIds.length} of {available.length}
        </span>
      </div>

      {composition && composition.missingCount > 0 && (
        <p
          className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-xs text-foreground"
          data-testid="findings-missing-warning"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-3.5 w-3.5" />
          <span>
            {composition.missingCount} selected finding(s) are no longer available and will not
            appear in the report.
          </span>
        </p>
      )}

      {!composition?.findingsComposed && (
        <p className="text-xs text-muted-foreground" data-testid="findings-legacy-notice">
          This report has not been composed yet, so it currently includes every finding. Save to
          confirm your selection.
        </p>
      )}

      {available.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="findings-none-available">
          This inspection has no findings to include.
        </p>
      ) : (
        <>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDraft(available.map((f) => f.id))}
                disabled={busy || selectedIds.length === available.length}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                data-testid="select-all-findings"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setDraft([])}
                disabled={busy || selectedIds.length === 0}
                className="rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                data-testid="clear-all-findings"
              >
                Clear all
              </button>
            </div>
          )}

          <ul className="space-y-1.5" data-testid="available-findings-list">
            {available.map((f) => {
              const checked = selectedIds.includes(f.id);
              return (
                <li key={f.id}>
                  <label
                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2 hover:bg-muted"
                    data-testid={`available-finding-${f.id}`}
                  >
                    {/* Real checkbox: keyboard-operable, labelled, and never the
                        only signal — the tick and the ordered list both reflect it. */}
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={checked}
                      disabled={!canEdit || busy}
                      onChange={() => toggle(f.id)}
                      aria-label={`Include finding ${f.findingNumber}: ${f.title}`}
                      data-testid={`toggle-finding-${f.id}`}
                    />
                    <span
                      aria-hidden="true"
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border'
                      } peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-ring`}
                    >
                      {checked && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" />}
                    </span>
                    <span className="w-6 shrink-0 text-xs text-muted-foreground">
                      #{f.findingNumber}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground">{f.title}</span>
                      {f.componentArea ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {f.componentArea}
                        </span>
                      ) : null}
                    </span>
                    <SeverityChip severity={f.severity} />
                  </label>
                </li>
              );
            })}
          </ul>

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Order in report
            </h4>
            {selectedFindings.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="findings-empty-selection">
                No findings selected — the report will not contain a findings section.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-1.5" data-testid="selected-findings-list">
                    {selectedFindings.map((f, i) => (
                      <SortableSelectedFinding
                        key={f.id}
                        finding={f}
                        index={i}
                        canEdit={canEdit && !busy}
                        onRemove={() => toggle(f.id)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {canEdit && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => saveMutation.mutate(selectedIds)}
                disabled={busy || !dirty}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                data-testid="save-report-findings"
              >
                {busy ? 'Saving…' : 'Save findings'}
              </button>
              {dirty && !busy && (
                <span className="text-xs text-muted-foreground" data-testid="findings-unsaved">
                  Unsaved changes
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
